import {
  Button,
  CircularProgress,
  ErrorMessage,
  Select,
  TextField,
  toast,
} from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { ToolType, VideoSummaryForm } from "api/resources/aiTools/types"
import { APIError } from "api/utils"
import Paper from "features/AItools/components/Paper"
import { getToolName, setLastUsedToolType } from "features/AItools/helper"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { handleErrors } from "utils/helpers"
import useDebounce from "utils/hooks/useDebounce"

import { VideoSummaryReturnType } from "../useVideoSummary"
import TimeRangeSlider from "./TimeRangeSlider"

const LANGUAGE_LIST = [
  { label: "English", value: "English" },
  { label: "Hindi", value: "Hindi" },
]

const toolType = ToolType.videoSummary

type Props = Pick<VideoSummaryReturnType, "generateTool">

const ProForm = ({ generateTool }: Props) => {
  const [duration, setDuration] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<VideoSummaryForm>({
    defaultValues: { language: LANGUAGE_LIST[0].value },
  })

  const videoUrl = useDebounce(watch("url"))

  const retrieveVideoDuration = useMutation({
    mutationFn: async (url: string) => {
      setDuration(null)
      return api.aiTools.retrieveVideoDuration({ data: { url } })
    },
    onSuccess: res => {
      setDuration(res.duration)
    },
    onError: (err: any) => {
      if (err instanceof APIError) {
        if ((err?.statusCode || 0) >= 500) {
          toast.error("We're unable to process your request", {
            description: "Please try again later",
          })
          return
        }
        if (err.errors.message) {
          toast.error(err.errors.message)
          return
        }
        if (err.errors.fieldErrors) {
          handleErrors(err, { setter: setError })
          return
        }
      }
      toast.error("We're unable to process your request", {
        description: "Please try again later",
      })
      setDuration(null)
    },
  })

  const { checkGenerationAvailability, aiToolsUsageQuery } =
    useSuraasaPlusContext()

  useEffect(() => {
    if (videoUrl) {
      retrieveVideoDuration.mutate(videoUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl])

  // eslint-disable-next-line no-warning-comments
  // TODO: Open get suraasa plus sheet
  // const onSubmit = handleSubmit(async data => {
  //   console.log("submitting", isActive)
  //   if (!isActive) {
  //     console.log("is inActive")
  //     showGetPlusSheet()
  //     return
  //   }

  //   await generateTool.mutateAsync({ data, setError })
  // })
  const onSubmit = handleSubmit(async data => {
    setLastUsedToolType({ toolType: toolType })

    checkGenerationAvailability(async () => {
      await generateTool.mutateAsync({ data, setError })
      aiToolsUsageQuery?.refetch()
    })
  })

  return (
    <Paper>
      <form onSubmit={onSubmit} className="flex w-full flex-col gap-2">
        <TextField
          required
          label="Video URL"
          endIcon={
            retrieveVideoDuration.isLoading ? <CircularProgress /> : undefined
          }
          placeholder="Ex: https://www.youtube.com/watch?v=4WTtDj23Sec"
          errors={errors.url?.message}
          {...register("url", {
            required: { value: true, message: "Required" },
          })}
        />
        <Controller
          control={control}
          name="language"
          render={({ field: { value, onChange, onBlur } }) => (
            <Select
              required
              errors={errors.language?.message}
              label="language"
              options={LANGUAGE_LIST}
              placeholder="Ex: English"
              mountOnBody
              onBlur={onBlur}
              value={LANGUAGE_LIST.find(c => c.value === value)}
              onChange={val => onChange(val?.value)}
            />
          )}
          rules={{
            required: { value: true, message: "Required" },
          }}
        />
        <Controller
          control={control}
          name="startTime"
          render={({ field: { onChange: onChangeStartTime } }) => (
            <Controller
              control={control}
              name="endTime"
              render={({ field: { onChange: onChangeEndTime } }) => (
                <>
                  <TimeRangeSlider
                    minTime="00:00:00"
                    maxTime={duration}
                    onChangeMin={min => onChangeStartTime(min)}
                    onChangeMax={max => onChangeEndTime(max)}
                    disabled={!videoUrl || !duration}
                  />
                  {(errors.startTime || errors.endTime) && (
                    <ErrorMessage
                      title={
                        errors.startTime?.message || errors.endTime?.message
                      }
                    />
                  )}
                </>
              )}
              rules={{
                required: { value: true, message: "End time is required" },
              }}
            />
          )}
          rules={{
            required: { value: true, message: "Start time is required" },
          }}
        />

        <Button
          type="submit"
          loading={isSubmitting}
          disabled={!duration}
          color="black"
        >
          Generate {getToolName(toolType)}
        </Button>
      </form>
    </Paper>
  )
}

export default ProForm
